import { AuthEvent } from '../icaps/useIcaps';
import { Auth0Event } from '../useAuth0';

import { AxiosError } from 'axios';
import { LoyaltyCard, MenuData } from '../../components/Modals/LoginModal/menu-types';

export type UnifiedAuthState =
  | { status: 'CheckingIfLoggedIn' }
  | { status: 'Unauthenticated'; error?: AxiosError; isLoggingIn: boolean }
  | { status: 'Reminder'; error?: AxiosError; isLoggingIn: boolean }
  | { status: 'ReminderMergeCheck' }
  | { status: 'Loyalty'; data: LoyaltyCard }
  | { status: 'Menu'; data: MenuData }
  | { status: 'MenuLoading' }
  | { status: 'MenuError'; error: Error }
  | { status: 'LogoutError'; error: Error; data?: MenuData }
  | { status: 'Closed' };

type SideEffects = {
  clearMenuCache: () => void;
  goToCheckout: () => void;
  goToMissingInfo: () => void;
};

// Pure function to compute the next auth state given an auth event and previous state.
// Returns undefined if no state change should occur.
export function authStateTransition(
  ev: AuthEvent | Auth0Event,
  prev: UnifiedAuthState,
  sideEffects: SideEffects,
): UnifiedAuthState {
  const authEventType = ev.status;

  if (prev.status === 'Closed') {
    // no state changes when closed
    return prev;
  }

  if (authEventType === 'validation-failure') {
    sideEffects.goToMissingInfo();
    return prev;
  }

  if (authEventType === 'initial-auth-check-true' && prev.status === 'Reminder') {
    sideEffects.goToCheckout();
    return prev;
  }

  if (authEventType === 'log-out-attempt-pending') {
    return prev;
  }

  if (prev.status === 'Reminder') {
    switch (authEventType) {
      case 'log-in-attempt-pending':
        return { ...prev, isLoggingIn: true };

      case 'initial-auth-check-pending':
      case 'initial-auth-check-false':
        return {
          ...prev,
          isLoggingIn: false,
        };
      case 'log-in-attempt-failure':
        return {
          ...prev,
          isLoggingIn: false,
          error: ev.error,
        };

      case 'log-in-attempt-success':
        return { status: 'ReminderMergeCheck' };
    }
  }

  switch (authEventType) {
    case 'initial-auth-check-pending':
      return { status: 'CheckingIfLoggedIn' };

    case 'log-in-attempt-failure':
      return {
        status: 'Unauthenticated',
        isLoggingIn: false,
        error: ev.error,
      };

    case 'initial-auth-check-false':
      return {
        status: 'Unauthenticated',
        isLoggingIn: false,
      };

    case 'initial-auth-check-true':
      return { status: 'MenuLoading' };

    case 'log-in-attempt-pending':
      return { status: 'Unauthenticated', isLoggingIn: true };

    case 'log-in-attempt-success':
      return { status: 'Closed' };

    case 'log-out-attempt-failure':
      return {
        status: 'LogoutError',
        error: ev.error,
        data: prev.status === 'Menu' ? prev.data : undefined,
      };

    case 'log-out-attempt-success':
      sideEffects.clearMenuCache();
      return { status: 'Closed' };

    default:
      authEventType satisfies never;
      console.warn('Unhandled auth event type in authStateTransition:', authEventType);
      return prev;
  }
}
